import type {
  FormVariables,
  FormData,
} from './../../ContactForm/ContactFormJSON.types';

const isProductionSite =
  window.location.href === 'https://www.wonderfulseedlesslemons.com/contact';

const contactFormVariables: FormVariables = {
  endpoint: isProductionSite
    ? 'https://cf.wonderful.com'
    : 'https://dev-cf.wonderful.com', // Link for dev form endpoint: https://dev-cf.wonderful.com
  routingStatus: isProductionSite ? 'True' : 'false', // True for prod (with capital 'T'). false for dev
  errorMessage: 'Please correct the fields in red.',
  noticeMessage: '*Required',
};

const hiddenFields: FormData = {
  visibleFieldsetDependencies: [['form_id', 'default']],
  legend: '',
  fields: [
    {
      type: 'hidden',
      label: 'Form ID',
      name: 'form_id',
      validationMessage: 'Please enter form id',
      required: true,
      defaultValue: 'seedless_lemons',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
  ],
};

const nameFields: FormData = {
  visibleFieldsetDependencies: [['first_name', 'default']],
  legend: '',
  fields: [
    {
      type: 'text',
      label: 'First Name*:',
      name: 'first_name',
      validationMessage: 'Please enter first name',
      required: true,
      defaultValue: '',
      autocomplete: 'given-name',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
    {
      type: 'text',
      label: 'Surname*:',
      name: 'last_name',
      validationMessage: 'Please enter surname',
      required: true,
      defaultValue: '',
      autocomplete: 'family-name',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
  ],
};

const credentialFields: FormData = {
  visibleFieldsetDependencies: [['first_name', 'default']],
  legend: '',
  fields: [
    {
      type: 'text',
      label: 'Title:',
      name: 'person_title',
      validationMessage: 'Please enter a title',
      required: false,
      defaultValue: '',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
    {
      type: 'text',
      label: 'Company:',
      name: 'company_name',
      validationMessage: 'Please enter a Company name',
      required: false,
      defaultValue: '',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
  ],
};

const contactFields: FormData = {
  visibleFieldsetDependencies: [['first_name', 'default']],
  legend: '',
  fields: [
    {
      type: 'text',
      label: 'Phone (XXX-XXX-XXXX):',
      name: 'phone_number',
      validationMessage: 'Please enter a valid phone number',
      required: false,
      defaultValue: '',
      autocomplete: 'tel',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
    {
      type: 'email',
      label: 'Email Address (name@domain.com)*:',
      name: 'user_email',
      validationMessage: 'Please enter a valid email',
      required: true,
      defaultValue: '',
      autocomplete: 'email',
      size: {
        mobile: 12,
        tablet: 6,
        desktop: 6,
      },
    },
  ],
};

const contactReasons: FormData = {
  visibleFieldsetDependencies: [['contact_reason', 'default']],
  legend: '',
  fields: [
    {
      type: 'select',
      label: 'Contact Reason*:',
      name: 'contact_reason',
      validationMessage: 'Contact reason must be selected',
      required: true,
      defaultValue: 'default',
      size: {
        mobile: 12,
        tablet: 12,
        desktop: 12,
      },
      optgroups: [
        {
          label: '',
          options: [
            { label: 'Select one', value: 'default' },
            { label: 'General Comment', value: 'General Comment' },
            { label: 'Complaint', value: 'Complaint' },
            {
              label: 'Public Relations/Media',
              value: 'Public Relations/Media',
            },
            { label: 'Sales', value: 'Sales' },
          ],
        },
      ],
    },
  ],
};

const complaintFields: FormData = {
  visibleFieldsetDependencies: [['contact_reason', 'Complaint']],
  legend: '',
  fields: [
    {
      type: 'text',
      label: 'Retailer purchased from:',
      name: 'retailer_purchased_from',
      validationMessage: 'Please enter a retailer purchased from',
      required: false,
      defaultValue: '',
      size: {
        mobile: 12,
        tablet: 12,
        desktop: 12,
      },
    },
    {
      type: 'select',
      label: 'Bag Size*:',
      name: 'bag_size',
      validationMessage: 'Please select a bag size',
      required: true,
      defaultValue: 'default',
      size: {
        mobile: 12,
        tablet: 12,
        desktop: 12,
      },
      optgroups: [
        {
          label: '',
          options: [
            { label: 'Please choose a bag size', value: 'default' },
            { label: '1lb bag', value: '1lb bag' },
            { label: '2lb bag', value: '2lb bag' },
            { label: '3lb bag', value: '3lb bag' },
            { label: '5lb bag', value: '5lb bag' },
          ],
        },
      ],
    },
  ],
};

const publicRelationsMediaFields: FormData = {
  visibleFieldsetDependencies: [['contact_reason', 'Public Relations/Media']],
  legend: '',
  fields: [
    {
      type: 'text',
      label: 'Media Outlet*:',
      name: 'media_outlet',
      validationMessage: 'Please describe the Media Outlet',
      required: true,
      defaultValue: '',
      size: {
        mobile: 12,
        tablet: 12,
        desktop: 12,
      },
    },
  ],
};

const salesFields: FormData = {
  visibleFieldsetDependencies: [['contact_reason', 'Sales']],
  legend: '',
  fields: [
    {
      type: 'select',
      label: 'Type of Company*:',
      name: 'company_type',
      validationMessage: 'Please select a type of company',
      required: true,
      defaultValue: 'default',
      size: {
        mobile: 12,
        tablet: 12,
        desktop: 12,
      },
      optgroups: [
        {
          label: '',
          options: [
            { label: 'Please select a type of company', value: 'default' },
            { label: 'Restaurant Chain', value: 'Restaurant Chain' },
            {
              label: 'Hotel / Motel / Resort Club',
              value: 'Hotel / Motel / Resort Club',
            },
            {
              label: 'School / College / University',
              value: 'School / College / University',
            },
            { label: 'Foodservice – Other', value: 'Foodservice – Other' },
            { label: 'Manufacturer', value: 'Manufacturer' },
            { label: 'Distributor', value: 'Distributor' },
            { label: 'Wholesaler', value: 'Wholesaler' },
            { label: 'Broker', value: 'Broker' },
            { label: 'Other', value: 'Other' },
          ],
        },
      ],
    },
  ],
};

const messageField: FormData = {
  visibleFieldsetDependencies: [['message', 'default']],
  legend: '',
  fields: [
    {
      type: 'textarea',
      label: 'Comments / Questions*:',
      name: 'message',
      validationMessage: 'Please enter a valid message',
      defaultValue: '',
      required: true,
      maxLength: 5000,
      size: {
        mobile: 12,
        tablet: 12,
        desktop: 12,
      },
    },
  ],
};

export const ContactFormJSON = {
  formId: 'seedless-lemons',
  formVariables: contactFormVariables,
  data: [
    hiddenFields,
    nameFields,
    credentialFields,
    contactFields,
    contactReasons,
    complaintFields,
    publicRelationsMediaFields,
    salesFields,
    messageField,
  ],
};

export default ContactFormJSON;
// export {};
